import {
  alpha,
  Box,
  Button,
  Chip,
  Fade,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { SubNavAppBar } from "components/AppBar";
import { ControlledBigTooltip } from "components/ControlledBigTooltip";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { Issue } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import pluralize from "pluralize";
import { useMemo, useState } from "react";
import { Link, matchPath, useLocation, useParams } from "react-router-dom";

import {
  isAddressIssue,
  isContactIssue,
  isCredentialIssue,
  isFacilityDocumentIssue,
  isLocalAgencyIssue,
  isStateInformationIssue,
  useFacility,
  useFacilityProfileIssuesV2,
} from "../useFacility";
import { ADDRESS_SECTION_ID, ADDRESS_SECTION_ICON } from "./AddressSection";
import { BASICS_SECTION_ID, BASICS_SECTION_ICON } from "./BasicsSection";
import {
  FACILITY_CONTACTS_SECTION_ID,
  FACILITY_CONTACTS_SECTION_ICON,
} from "./FacilityContactsSection";
import {
  FACILITY_CREDENTIALS_SECTION_ID,
  FACILITY_CREDENTIALS_SECTION_ICON,
} from "./FacilityCredentialsSection";
import {
  FACILITY_DOCUMENTS_SECTION_ID,
  FACILITY_DOCUMENTS_SECTION_ICON,
} from "./FacilityDocumentsSection";
import { LOCAL_AGENCIES_SECTION_ID, LOCAL_AGENCIES_SECTION_ICON } from "./LocalAgenciesSection";
import { RCRA_PROFILE_SECTION_ID, RCRA_PROFILE_SECTION_ICON } from "./RcraProfileSection";
import {
  STATE_INFORMATION_SECTION_ID,
  STATE_INFORMATION_SECTION_ICON,
} from "./StateInformationSection";

interface SectionLink {
  id: string;
  label: string;
  icon: string;
}

interface SectionIssueGroup extends SectionLink {
  issues: Issue[];
}

function buildSections({ showRcraProfile }: { showRcraProfile: boolean }): SectionLink[] {
  const baseSections: SectionLink[] = [
    { id: BASICS_SECTION_ID, label: "Basics", icon: BASICS_SECTION_ICON },
    { id: ADDRESS_SECTION_ID, label: "Address", icon: ADDRESS_SECTION_ICON },
    { id: LOCAL_AGENCIES_SECTION_ID, label: "Local Agencies", icon: LOCAL_AGENCIES_SECTION_ICON },
    {
      id: STATE_INFORMATION_SECTION_ID,
      label: "State Information",
      icon: STATE_INFORMATION_SECTION_ICON,
    },
    {
      id: FACILITY_CONTACTS_SECTION_ID,
      label: "Facility Contacts",
      icon: FACILITY_CONTACTS_SECTION_ICON,
    },
  ];

  if (showRcraProfile) {
    baseSections.push({
      id: RCRA_PROFILE_SECTION_ID,
      label: "RCRA Profile",
      icon: RCRA_PROFILE_SECTION_ICON,
    });
  }

  return [
    ...baseSections,
    {
      id: FACILITY_DOCUMENTS_SECTION_ID,
      label: "Facility Documents",
      icon: FACILITY_DOCUMENTS_SECTION_ICON,
    },
    {
      id: FACILITY_CREDENTIALS_SECTION_ID,
      label: "Credentials",
      icon: FACILITY_CREDENTIALS_SECTION_ICON,
    },
  ];
}

export const SECTION_NAV_ID = "section-nav";

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  const nav = document.getElementById(SECTION_NAV_ID);
  if (element && nav) {
    const navBottom = nav.getBoundingClientRect().bottom;
    const elementTop = element.getBoundingClientRect().top;
    const scrollBy = elementTop - navBottom - 16;
    window.scrollBy({ top: scrollBy, behavior: "smooth" });
  }
};

function groupIssuesBySection(
  issues: Issue[],
  isCA: boolean,
  sections: SectionLink[]
): SectionIssueGroup[] {
  const sectionIssues: Record<string, Issue[]> = {
    [BASICS_SECTION_ID]: [] as Issue[],
    [ADDRESS_SECTION_ID]: [] as Issue[],
    [LOCAL_AGENCIES_SECTION_ID]: [] as Issue[],
    [STATE_INFORMATION_SECTION_ID]: [] as Issue[],
    [FACILITY_CONTACTS_SECTION_ID]: [] as Issue[],
    [FACILITY_DOCUMENTS_SECTION_ID]: [] as Issue[],
    [FACILITY_CREDENTIALS_SECTION_ID]: [] as Issue[],
  };

  for (const issue of issues) {
    if (isContactIssue(issue)) {
      sectionIssues[FACILITY_CONTACTS_SECTION_ID].push(issue);
      continue;
    }

    if (isFacilityDocumentIssue(issue)) {
      sectionIssues[FACILITY_DOCUMENTS_SECTION_ID].push(issue);
      continue;
    }

    if (isCredentialIssue(issue)) {
      sectionIssues[FACILITY_CREDENTIALS_SECTION_ID].push(issue);
      continue;
    }

    if (isStateInformationIssue(issue, { omitStateFields: isCA })) {
      sectionIssues[STATE_INFORMATION_SECTION_ID].push(issue);
      continue;
    }

    if (isLocalAgencyIssue(issue)) {
      sectionIssues[LOCAL_AGENCIES_SECTION_ID].push(issue);
      continue;
    }

    if (isAddressIssue(issue)) {
      sectionIssues[ADDRESS_SECTION_ID].push(issue);
      continue;
    }

    sectionIssues[BASICS_SECTION_ID].push(issue);
  }

  return sections
    .map((section) => ({ ...section, issues: sectionIssues[section.id] ?? [] }))
    .filter((section) => section.issues.length > 0);
}

const ComplianceIssueButton: React.FC<{ sections: SectionLink[] }> = ({ sections }) => {
  const { data } = useFacility();
  const isCA = data?.facility?.state === "CA";
  const issues = useFacilityProfileIssuesV2({ omitStateFields: isCA });
  const [open, setOpen] = useState(false);
  const groupedIssues = useMemo(
    () => groupIssuesBySection(issues, isCA, sections),
    [issues, isCA, sections]
  );

  const label = useMemo(() => {
    const count = issues.length;
    return `${count > 0 ? count : "No"} compliance ${pluralize("issue", count)}`;
  }, [issues.length]);

  return (
    <ControlledBigTooltip
      open={open}
      onClose={() => setOpen(false)}
      placement="bottom-end"
      slots={{ transition: Fade }}
      slotProps={{ tooltip: { sx: { maxWidth: (theme) => theme.spacing(65) } } }}
      title={
        <Stack
          gap={1}
          sx={{
            marginBottom: 0.5,
            maxHeight: (theme) => theme.spacing(75),
            overflowY: "auto",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} fontSize="12px">
            {label}
          </Typography>
          {groupedIssues.map((group) => (
            <Box key={group.id}>
              <Button
                size="small"
                onClick={() => {
                  scrollToSection(group.id);
                  setOpen(false);
                }}
                sx={{
                  px: 0,
                  minWidth: 0,
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "inherit",
                }}
              >
                {group.icon} {group.label}
              </Button>
              <Typography component="ul" sx={{ m: 0, pl: 2, fontSize: "12px" }}>
                {group.issues.map((issue, index) => (
                  <li key={`${group.id}-${index}`}>{issue.message}</li>
                ))}
              </Typography>
            </Box>
          ))}
        </Stack>
      }
    >
      <Button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={issues.length === 0}
        variant="text"
        size="small"
        sx={{ color: "text.secondary" }}
      >
        {label}
      </Button>
    </ControlledBigTooltip>
  );
};

export const FacilityHeaderV2: React.FC = () => {
  const theme = useTheme();
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();
  const { pathname } = useLocation();
  const { featureFlags } = useFeatureFlags();
  const sections = useMemo(
    () =>
      buildSections({
        showRcraProfile: !!featureFlags[FeatureFlagKey.RcraFacilityProfile],
      }),
    [featureFlags]
  );

  const basePath = `/o/${tenantId}/facilities/${facilityId}`;
  const tabs = [
    { label: "Facility Overview", to: `${basePath}/profile` },
    { label: "Tasks", to: `${basePath}/tasks` },
  ];

  const activeTabIndex = tabs.findIndex(({ to }) => matchPath(`${to}/*`, pathname));
  const isProfileTab = activeTabIndex === 0;

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <SubNavAppBar id={SECTION_NAV_ID}>
      <Tabs
        value={activeTabIndex >= 0 ? activeTabIndex : 0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {tabs.map((tab, index) => (
          <Tab key={index} label={tab.label} component={Link} to={tab.to} sx={{ px: 3 }} />
        ))}
      </Tabs>
      {isProfileTab && (
        <Box px={3} py={1.5} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Sections
            </Typography>
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent={isMobile ? "flex-start" : "space-between"}
              alignItems={isMobile ? "flex-start" : "center"}
              gap={2}
            >
              <Stack direction="row" gap={1} flexWrap="wrap">
                {sections.map((section) => (
                  <Chip
                    key={section.id}
                    label={`${section.icon} ${section.label}`}
                    onClick={() => scrollToSection(section.id)}
                    size="small"
                    sx={{
                      backgroundColor: alpha(
                        theme.palette.info.main,
                        theme.palette.action.selectedOpacity
                      ),
                      color: theme.palette.text.primary,
                    }}
                  />
                ))}
              </Stack>
              <ComplianceIssueButton sections={sections} />
            </Stack>
          </Stack>
        </Box>
      )}
    </SubNavAppBar>
  );
};
